import type {
  CreateEstimationRequest,
  CreateProjectRequest,
  EstimationItemResponse,
  EstimationModelResponse,
  EstimationUserAssignmentResponse,
  MethodologyResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';
import type { AdditionalField, PhaseResponse } from '@/types';

export interface EstimationState {
  selectedEstimationModel: EstimationModelResponse | null;
  phaseResponses: Partial<PhaseResponse[]>;
  methodologiesList: MethodologyResponse[];
  planningResponsibleId: string | undefined;
  projectReviewerId: string | undefined;
  additionalContributorsIds: string[];
  additionalFields: AdditionalField[];
  unsavedEstimation:
    | {
        estimation: CreateEstimationRequest;
        project: CreateProjectRequest;
      }
    | undefined;
  savedEstimation: ProjectWithEstimationResponse | undefined;
  estimationUserAssignments: EstimationUserAssignmentResponse[];
  detailedEstimationId: string | undefined;
}

export enum eActionTypes {
  SET_ESTIMATION = 'SET_ESTIMATION',
  SET_SELECTED_ESTIMATION_MODEL = 'SET_SELECTED_ESTIMATION_MODEL',
  SET_PHASE_RESPONSES = 'SET_PHASE_RESPONSES',
  SET_METHODOLOGIES = 'SET_METHODOLOGIES',
  SET_PROJECT = 'SET_PROJECT',
  SET_SAVED_ESTIMATION = 'SET_SAVED_ESTIMATION',
  SET_ESTIMATION_USER_ASSIGNMENTS = 'SET_ESTIMATION_USER_ASSIGNMENTS',
  SET_PLANNING_RESPONSIBLE_ID = 'SET_PLANNING_RESPONSIBLE_ID',
  SET_PROJECT_REVIEWER_ID = 'SET_PROJECT_REVIEWER_ID',
  SET_ADDITIONAL_CONTRIBUTORS = 'SET_ADDITIONAL_CONTRIBUTORS',
  SET_ADDITIONAL_FIELDS = 'SET_ADDITIONAL_FIELDS',
  REMOVE_ADDITIONAL_FIELD = 'REMOVE_ADDITIONAL_FIELD',
  RESET_ESTIMATION_STATE = 'RESET_ESTIMATION_STATE',
  SET_DETAILED_ESTIMATION_ID = 'SET_DETAILED_ESTIMATION_ID',
}

export interface IEstimationsActions {
  setEstimation: (estimation: Partial<CreateEstimationRequest>) => void;
  setSelectedEstimationModel: (estimationModel: EstimationModelResponse | null) => void;
  setMethodologies: (methodologies: Partial<MethodologyResponse[]>) => void;
  setPhaseResponses: (phaseResponses: Partial<(PhaseResponse | EstimationItemResponse)[]>) => void;
  setProject: (project: Partial<CreateProjectRequest>) => void;
  setSavedEstimation: (newEstimation: ProjectWithEstimationResponse) => void;
  setEstimationUserAssignments: (assignments: EstimationUserAssignmentResponse[]) => void;
  setPlanningResponsibleId: (planningResponsibleId: string) => void;
  setProjectReviewerId: (reviewerId: string) => void;
  setAdditionalContributors: (contributorsIds: string[]) => void;
  setAdditionalFields: (additionalFields: AdditionalField[]) => void;
  removeAdditionalField: (fieldId: string) => void;
  resetEstimationState: () => void;
  setDetailedEstimationId: (estimationId: string) => void;
}

export type Actions =
  | { type: eActionTypes.SET_ESTIMATION; payload: Partial<CreateEstimationRequest> }
  | { type: eActionTypes.SET_SELECTED_ESTIMATION_MODEL; payload: EstimationModelResponse | null }
  | { type: eActionTypes.SET_PHASE_RESPONSES; payload: Partial<PhaseResponse[]> }
  | { type: eActionTypes.SET_METHODOLOGIES; payload: MethodologyResponse[] }
  | { type: eActionTypes.SET_PROJECT; payload: Partial<CreateProjectRequest> }
  | { type: eActionTypes.SET_SAVED_ESTIMATION; payload: ProjectWithEstimationResponse }
  | {
      type: eActionTypes.SET_ESTIMATION_USER_ASSIGNMENTS;
      payload: EstimationUserAssignmentResponse[];
    }
  | { type: eActionTypes.SET_PLANNING_RESPONSIBLE_ID; payload: string }
  | { type: eActionTypes.SET_PROJECT_REVIEWER_ID; payload: string }
  | { type: eActionTypes.SET_ADDITIONAL_CONTRIBUTORS; payload: string[] }
  | { type: eActionTypes.SET_ADDITIONAL_FIELDS; payload: AdditionalField[] }
  | { type: eActionTypes.REMOVE_ADDITIONAL_FIELD; payload: string }
  | { type: eActionTypes.SET_DETAILED_ESTIMATION_ID; payload: string }
  | { type: eActionTypes.RESET_ESTIMATION_STATE };

export const initEstimationState: EstimationState = {
  selectedEstimationModel: null,
  phaseResponses: [],
  methodologiesList: [],
  planningResponsibleId: undefined,
  projectReviewerId: undefined,
  additionalContributorsIds: [],
  additionalFields: [],
  unsavedEstimation: undefined,
  savedEstimation: undefined,
  detailedEstimationId: undefined,
  estimationUserAssignments: [],
};
